import React from 'react'
import { FormattedMessage } from 'react-intl'
import {
  CoveredRecommendHelpfulYes,
  CoveredRecommendHelpfulNo,
  CoveredExploreOptionsClick
} from 'src/constants/trackingEvents'
import {
  Button,
  ExternalLinkButton,
  Panel,
  DisclaimersButton,
  Format,
  Text,
  Unless
} from 'src/Components'
import QuestionSingle from 'src/Components/homeowners/Questions/Single'
import { IconBotHeadCircle } from 'src/Components/shared/Icon'
import { QUESTIONS } from 'src/constants/messages'

import styles from './HomeInsuranceDetail.module.scss'

const DISCOUNT_PERCENT = 15
const RANGE_PERCENT = 200

const HomeInsuranceDetail = ({
  hideDetail,
  hasFeedback,
  submittedFeedback,
  handlePositiveFeedback,
  handleNegativeFeedback,
  trackCoveredButtonClick,
  coveredUri
}) => (
  <>
    <div className={styles.container}>
      <Button.Close className={styles.closeButton} onClick={hideDetail} />
      <Panel.Heading>
        <FormattedMessage
          id='Homeowner.homeInsurance.detailHeading'
          defaultMessage="Make sure you're fully covered and not overpaying"
        />
      </Panel.Heading>

      <Text
        className={styles.subheading}
        size='s'
        color='quiet'
        element='div'
        align='center'
      >
        <FormattedMessage
          id='Homeowner.homeInsurance.detailSubheading'
          defaultMessage='{boldText} for your insurance renewal statement -- you could reduce your payment or save on long-term expenses by shopping multiple carriers to ensure you’re getting the best value.'
          values={{
            boldText: (
              <Text weight='bold' color='quiet' element='span'>
                <FormattedMessage
                  id='Homeowner.homeInsurance.boldSubheading'
                  defaultMessage='Watch your mail or email'
                />
              </Text>
            )
          }}
        />
      </Text>

      <Panel className={styles.explainer}>
        <Text
          className={styles.explainerNumber}
          size='xxxl'
          weight='light'
          color='green'
          align='center'
          element='div'
        >
          <Format.Percentage value={DISCOUNT_PERCENT} />
        </Text>
        <Text
          className={styles.explainerText}
          size='s'
          color='quiet'
          align='left'
          element='div'
        >
          <FormattedMessage
            id='Homeowner.homeInsurance.detailPurchasePolicy'
            defaultMessage="If you purchase your policy 7 days before it's due, some carriers will discount up to {percentage}"
            values={{
              percentage: <Format.Percentage value={DISCOUNT_PERCENT} />
            }}
          />
        </Text>
      </Panel>
      <Panel className={styles.explainer}>
        <Text
          className={styles.explainerNumber}
          size='xxxl'
          weight='light'
          color='green'
          align='center'
          element='div'
        >
          <Format.Percentage value={RANGE_PERCENT} />
        </Text>
        <Text
          className={styles.explainerText}
          size='s'
          color='quiet'
          align='left'
          element='div'
        >
          <FormattedMessage
            id='Homeowner.homeInsurance.detailPriceDifference'
            defaultMessage='The price difference between carriers can range by {percentage} for the exact same home and coverage'
            values={{
              percentage: <Format.Percentage value={RANGE_PERCENT} />
            }}
          />
        </Text>
      </Panel>

      <Text
        className={styles.compareCTA}
        size='s'
        color='quiet'
        element='div'
        align='center'
      >
        <FormattedMessage
          id='Homeowner.homeInsurance.compareCTAOne'
          defaultMessage='Did you replace your roof recently? Some carriers reward you with a discount. Others offer home and auto insurance bundling. Some can help with AirBnb or home office insurance. Make sure to compare policies to find the best value.'
        />
        <FormattedMessage
          id='Homeowner.homeInsurance.compareCTATwo'
          defaultMessage='For a quick idea of your options, check out Covered, a trusted independent partner of Homebot.'
        />
      </Text>

      <Text className={styles.clickArea} element='div'>
        <ExternalLinkButton
          className={styles.button}
          href={coveredUri}
          onClick={trackCoveredButtonClick}
          track={CoveredExploreOptionsClick}
          trackOptions={{ eventLabel: 'HomeInsurance' }}
        >
          <FormattedMessage
            id='Homeowner.homeInsurance.exploreCTAButton'
            defaultMessage='Go to Covered'
          />
        </ExternalLinkButton>
      </Text>
    </div>

    <Unless condition={submittedFeedback || hasFeedback}>
      <div className={styles.feedback}>
        <Text size='s' element='div'>
          <IconBotHeadCircle className={styles.feedbackIcon} />
          <FormattedMessage
            id='Homeowner.homeInsurance.helpfulness'
            defaultMessage='Are these types of recommendations helpful to you?'
          />
          <span className={styles.feedbackBreak}>
            <Button
              className={styles.feedbackButton}
              onClick={handlePositiveFeedback}
              track={CoveredRecommendHelpfulYes}
              trackOptions={{ eventLabel: 'HomeInsurance' }}
            >
              <FormattedMessage
                id='Homeowner.homeInsurance.positiveFeedback'
                defaultMessage='Yes'
              />
            </Button>
            <Button
              className={styles.feedbackButton}
              onClick={handleNegativeFeedback}
              track={CoveredRecommendHelpfulNo}
              trackOptions={{ eventLabel: 'HomeInsurance' }}
            >
              <FormattedMessage
                id='Homeowner.homeInsurance.negativeFeedback'
                defaultMessage='No'
              />
            </Button>
          </span>
        </Text>
      </div>
    </Unless>

    <div className={styles.legal}>
      <DisclaimersButton className={styles.disclaimers} />
    </div>

    <Panel.Footer className={styles.footer}>
      <QuestionSingle
        disableCospo
        topic={QUESTIONS.insuranceTopic}
        question={QUESTIONS.insuranceRecommendation}
      />
    </Panel.Footer>
  </>
)

export default HomeInsuranceDetail
